import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
  useMutation,
  UseMutationOptions,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { useGuardianMessagingService } from '../../../../apis/guardian/sub-apis/useGuardianMessagingService';
import {
  MessageListResponse,
  ConversationListResponse,
  CreateMessagePayload,
  BroadcastMessagePayload,
  MessageWithRelations,
  MessageQueryParams,
} from '../../../../apis/messaging/messaging.interfaces';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

// Query keys
export const getGuardianAllMessagesKey = 'get-guardian-all-messages';
export const getGuardianDirectMessagesKey = 'get-guardian-direct-messages';
export const getGuardianBroadcastMessagesKey = 'get-guardian-broadcast-messages';
export const getGuardianConversationsKey = 'get-guardian-conversations';

// Get all messages hook for guardian (scoped to studentId)
export const useGuardianGetAllMessagesHook = (
  studentId: string,
  params: MessageQueryParams,
  options?: UseQueryOptions<MessageListResponse, TQueryError>
): UseQueryResult<MessageListResponse, TQueryError> => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  const queryKey = [getGuardianAllMessagesKey, studentId, params];

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 20 };
  return useQuery<MessageListResponse, TQueryError>({
    queryKey,
    queryFn: () => guardianMessagingService.getAllMessages(safeParams),
    enabled: options?.enabled !== undefined ? options.enabled : !!studentId,
    ...options,
  });
};

// Get direct messages hook for guardian
export const useGuardianGetDirectMessagesHook = (
  studentId: string,
  receiver_id: string,
  params: { page?: number; limit?: number },
  options?: UseQueryOptions<MessageListResponse, TQueryError>
): UseQueryResult<MessageListResponse, TQueryError> => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  const queryKey = [getGuardianDirectMessagesKey, studentId, receiver_id, params];

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 20 };
  return useQuery<MessageListResponse, TQueryError>({
    queryKey,
    queryFn: () => guardianMessagingService.getDirectMessages(receiver_id, safeParams),
    enabled: options?.enabled !== undefined ? options.enabled : !!studentId && !!receiver_id,
    ...options,
  });
};

// Get broadcast messages hook for guardian
export const useGuardianGetBroadcastMessagesHook = (
  studentId: string,
  class_id: string | undefined,
  params: { page?: number; limit?: number } = { page: 1, limit: 20 },
  options?: UseQueryOptions<MessageListResponse, TQueryError>
): UseQueryResult<MessageListResponse, TQueryError> => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  const queryKey = [getGuardianBroadcastMessagesKey, studentId, class_id, params];

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 20 };
  return useQuery<MessageListResponse, TQueryError>({
    queryKey,
    queryFn: () => guardianMessagingService.getBroadcastMessages(class_id, safeParams),
    enabled: options?.enabled !== undefined ? options.enabled : !!studentId,
    ...options,
  });
};

// Get conversations hook for guardian
export const useGuardianGetConversationsHook = (
  studentId: string,
  params: { page?: number; limit?: number },
  options?: UseQueryOptions<ConversationListResponse, TQueryError>
): UseQueryResult<ConversationListResponse, TQueryError> => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  const queryKey = [getGuardianConversationsKey, studentId, params];

  const safeParams = { ...params, page: params.page ?? 1, limit: params.limit ?? 20 };
  return useQuery<ConversationListResponse, TQueryError>({
    queryKey,
    queryFn: () => guardianMessagingService.getConversations(safeParams),
    enabled: options?.enabled !== undefined ? options.enabled : !!studentId,
    ...options,
  });
};

// Send direct message mutation hook for guardian
export const useGuardianSendDirectMessageHook = (
  studentId: string,
  options?: UseMutationOptions<
    MessageWithRelations,
    TMutationError,
    CreateMessagePayload & { sender_id?: string },
    unknown
  >
) => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  return useMutation({
    mutationFn: (payload: CreateMessagePayload & { sender_id?: string }) =>
      guardianMessagingService.sendDirectMessage(payload),
    ...options,
  });
};

// Send broadcast message mutation hook for guardian
export const useGuardianSendBroadcastMessageHook = (
  studentId: string,
  options?: UseMutationOptions<
    MessageWithRelations,
    TMutationError,
    BroadcastMessagePayload & { sender_id?: string },
    unknown
  >
) => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  return useMutation({
    mutationFn: (payload: BroadcastMessagePayload & { sender_id?: string }) =>
      guardianMessagingService.sendBroadcastMessage(payload),
    ...options,
  });
};

// Mark messages as read mutation hook for guardian
export const useGuardianMarkMessagesAsReadHook = (
  studentId: string,
  options?: UseMutationOptions<
    { success: boolean; updated_count: number },
    TMutationError,
    string[],
    unknown
  >
) => {
  const guardianMessagingService = useGuardianMessagingService(studentId);

  return useMutation({
    mutationFn: (message_ids: string[]) => guardianMessagingService.markMessagesAsRead(message_ids),
    ...options,
  });
};

// Main hook that exposes guardian messaging service methods
export const useGuardianMessagingHook = (studentId: string) => {
  return useGuardianMessagingService(studentId);
};
